import React, { useState, useEffect, useCallback, useMemo } from "react";
import AdminUserListing from "../../components/admin/AdminUserListing";
import { showSuccessToast, showErrorToast } from "../../utils/AlertUtils";
import { useLoading } from "../../context/LoadingContext";
import guLocale from "../../locales/gu";
import AdminUserService from "../../services/admin/AdminUserService";
import AdminUserViewModal from "../../components/admin/AdminUserViewModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faToggleOn , faToggleOff, faTrash } from "@fortawesome/free-solid-svg-icons";
import ConfirmationModal from "../../components/ConfirmationModal";

const AdminUser = () => {
  const locale = guLocale.admin.user;
  const { setIsLoading } = useLoading();
  const [filterText, setFilterText] = useState(""); // State for filter text
  const [data, setData] = useState([]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [userIdToDelete, setUserIdToDelete] = useState(null);

  const fetchListingData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await AdminUserService.getUsers();
      if(response && response.data) {
        setData(response.data);
      }
    } catch (error) {
      console.error("Error fetching users data", error);
      showErrorToast(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading]);

  useEffect(() => {
    fetchListingData();
  }, [fetchListingData]);

  const columns = [
    { name: locale.schoolNameLabel, selector: (row) => row.school_name, sortable: true },
    { name: locale.brcFullNameLabel, selector: (row) => row.brc_full_name, sortable: true },
    { name: locale.rojmelNameLabel, selector: (row) => row.block_name, sortable: true },
    { name: locale.schoolDiceCodeLabel, selector: (row) => row.school_dice_code, sortable: true },
    { name: locale.businessEmailLabel, selector: (row) => row.business_email, sortable: true },
    { name: locale.mobileNoLabel, selector: (row) => row.mobile_no, sortable: true },
    {
      name: locale.status,
      cell: (row) => (
        <span
          onClick={() => handleToggleStatus(row)}
          style={{ cursor: "pointer", fontSize: "1.5rem" }}
        >
          {row.isActive ? (
            <FontAwesomeIcon icon={faToggleOn} className="text-success" title={locale.active} />
          ) : (
            <FontAwesomeIcon icon={faToggleOff} className="text-danger" title={locale.inactive} />
          )}
        </span>
      ),
      width: "70px",
    },
    {
        name: locale.action,
        cell: (row) => (
        <>
            <button
            className="btn btn-info btn-sm mr-2" // Added info button for View
            onClick={() => handleView(row)} // handleView function
            >
            <FontAwesomeIcon icon={faEye} />
            </button>
            <button className="btn btn-danger btn-sm" onClick={() => handleDeleteUser(row._id)}>
              <FontAwesomeIcon icon={faTrash} />
            </button>
        </>
        ),
        width: "110px",
    },
  ];

  const filteredData = useMemo(() => {
    return data.filter((item) =>
      Object.values(item).some((value) =>
        value?.toString().toLowerCase().includes(filterText.toLowerCase())
      )
    );
  }, [data, filterText]);

  const handleFilterChange = useCallback((e) => {
    setFilterText(e.target.value); // Only update filterText state
  }, []);

  const handleView = async (user) => {
    setIsLoading(true);
    try {
      const response = await AdminUserService.getUserById(user._id); // API call to get ledger by ID
      if (response && response.statusCode === 200) {
        setCurrentUser(response.data); // Set the current ledger with fetched data
        setShowViewModal(true); // Show the modal to view details
      }
    } catch (error) {
      console.error("Error fetching ledger", error);
      showErrorToast(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalClose = () => {
    setShowViewModal(false);
    setCurrentUser(null);
  };

  const handleToggleStatus = async (user) => {
    setIsLoading(true);
    try {
      const updatedStatus = !user.isActive;
      await AdminUserService.activateUser(user._id, updatedStatus);
      showSuccessToast(`વપરાશકર્તા સફળતાપૂર્વક ${updatedStatus ? locale.active : locale.inactive} કરવામાં આવ્યો છે`);
      fetchListingData();
    } catch (error) {
      showErrorToast(error.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  const confirmDelete = async () => {
    try {
      setIsLoading(true);
      const response = await AdminUserService.deleteUser(userIdToDelete);
      if (response && response.statusCode === 200) {
        showSuccessToast(locale.user_deleted_successfuly);
        fetchListingData();
      }
    } catch (error) {
      console.error("Error deleting User", error);
      showErrorToast(error.message);
    } finally {
      setIsLoading(false);
      setShowModal(false);
      setUserIdToDelete(null);
    }
  };

  const handleDeleteUser = async (userId) => {
    setUserIdToDelete(userId);
    setShowModal(true);
  };

  return (
    <div>
      <AdminUserListing
        columns={columns}
        data={filteredData}
        filterText={filterText}
        handleFilterChange={handleFilterChange}
      />
      <AdminUserViewModal
        show={showViewModal}
        handleClose={handleModalClose}
        initialData={currentUser}
      />
      <ConfirmationModal
        show={showModal}
        title={locale.delete_user}
        message={locale.are_you_sure_you_want_to_delete_user}
        onConfirm={confirmDelete}
        onCancel={() => setShowModal(false)}
      />
    </div>
  );
};

export default AdminUser;